import { find, groupBy, sortBy, uniqBy } from 'lodash-es';

// Local structural geo types (kept here to avoid a dependency on the map-tools
// library from this low-level platform util, which would create a cycle).
type LatLngLiteral = { lat: number; lng: number; alt?: number };
type LatLngTuple = [number, number] | [number, number, number];
type LatLngBoundsLiteral = LatLngTuple[];

import { TranslationId } from '../types/TranslationId';

const defaultCountry = 'de';
const defaultLocale = 'en_de';

export enum MapProvider {
  Bing = 'Bing',
  Google = 'Google',
  Mapbox = 'Mapbox',
}

interface SupportedCountry {
  countryTranslationKey: string;
  country: string;
  languages: string[];
}

export type SupportedCountries = {
  countryTranslationKey: TranslationId;
  languageTranslationKey: TranslationId;
  countryCode: string;
  languageCode: string;
  locale: string;
  phoneCode: TranslationId;
  order: number;
};

const readLocalStorageJson = <T>(key: string): T | undefined => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
};

export class CountryUtils {
  // language ISO_639_1
  static get LANGUAGE() {
    return {
      GERMAN: 'de',
      ENGLISH: 'en',
      FRENCH: 'fr',
      DUTCH: 'nl',
      POLISH: 'pl',
      PORTUGUESE: 'pt',
      UKRAINIAN: 'uk',
      SPANISH: 'es',
      HUNGARIAN: 'hu',
      CZECH: 'cs',
      JAPANESE: 'ja',
      RUSSIAN: 'ru',
      ITALIAN: 'it',
      LITHUANIAN: 'lt',
      HINDI: 'hi',
      PUNJABI: 'pa',
      TELUGU: 'te',
      ROMANIAN: 'ro',
      TURKISH: 'tr',
      GREEK: 'el',
    };
  }

  // country ISO_3166_1_ALPHA_2
  static get COUNTRY() {
    return {
      ARGENTINA: 'ar',
      AUSTRALIA: 'xx',
      AUSTRIA: 'at',
      BELGIUM: 'be',
      BRAZIL: 'br',
      CANADA: 'ca',
      CZECH_REPUBLIC: 'cz',
      FRANCE: 'fr',
      GERMANY: 'de',
      GREAT_BRITAIN: 'gb',
      HUNGARY: 'hu',
      INDIA: 'in',
      IRELAND: 'ie',
      ITALY: 'it',
      JAPAN: 'jp',
      LITHUANIAN: 'lt',
      LITHUANIA: 'lt',
      NETHERLAND: 'nl',
      POLAND: 'pl',
      ROMANIA: 'ro',
      RUSSIA: 'ru',
      SPAIN: 'es',
      TURKEY: 'tr',
      UKRAINE: 'ua',
      USA: 'us',
    };
  }

  static getProvincesForCanada() {
    return [
      { label: 'CANADA.STATE.ALBERTA', key: 'AB' },
      { label: 'CANADA.STATE.BRITISH_COLUMBIA', key: 'BC' },
      { label: 'CANADA.STATE.MANITOBA', key: 'MB' },
      { label: 'CANADA.STATE.NEW_BRUNSWICK', key: 'NB' },
      { label: 'CANADA.STATE.NEWFOUNDLAND_AND_LABRADOR', key: 'NL' },
      { label: 'CANADA.STATE.NORTHWEST_TERRITORIES', key: 'NT' },
      { label: 'CANADA.STATE.NOVA_SCOTIA', key: 'NS' },
      { label: 'CANADA.STATE.NUNAVUT', key: 'NU' },
      { label: 'CANADA.STATE.ONTARIO', key: 'ON' },
      { label: 'CANADA.STATE.PRINCE_EDWARD_ISLAND', key: 'PE' },
      { label: 'CANADA.STATE.QUEBEC', key: 'QC' },
      { label: 'CANADA.STATE.SASKATCHEWAN', key: 'SK' },
      { label: 'CANADA.STATE.YUKON', key: 'YT' },
    ];
  }

  static getProvincesForUsa() {
    return [
      { label: 'USA.STATE.ALABAMA', key: 'AL' },
      { label: 'USA.STATE.ALASKA', key: 'AK' },
      { label: 'USA.STATE.ARIZONA', key: 'AZ' },
      { label: 'USA.STATE.ARKANSAS', key: 'AR' },
      { label: 'USA.STATE.CALIFORNIA', key: 'CA' },
      { label: 'USA.STATE.COLORADO', key: 'CO' },
      { label: 'USA.STATE.CONNECTICUT', key: 'CT' },
      { label: 'USA.STATE.DISTRICT_OF_COLUMBIA', key: 'DC' },
      { label: 'USA.STATE.DELAWARE', key: 'DE' },
      { label: 'USA.STATE.FLORIDA', key: 'FL' },
      { label: 'USA.STATE.GEORGIA', key: 'GA' },
      { label: 'USA.STATE.HAWAII', key: 'HI' },
      { label: 'USA.STATE.IDAHO', key: 'ID' },
      { label: 'USA.STATE.ILLINOIS', key: 'IL' },
      { label: 'USA.STATE.INDIANA', key: 'IN' },
      { label: 'USA.STATE.IOWA', key: 'IA' },
      { label: 'USA.STATE.KANSAS', key: 'KS' },
      { label: 'USA.STATE.KENTUCKY', key: 'KY' },
      { label: 'USA.STATE.LOUISIANA', key: 'LA' },
      { label: 'USA.STATE.MAINE', key: 'ME' },
      { label: 'USA.STATE.MARYLAND', key: 'MD' },
      { label: 'USA.STATE.MASSACHUSETTS', key: 'MA' },
      { label: 'USA.STATE.MICHIGAN', key: 'MI' },
      { label: 'USA.STATE.MINNESOTA', key: 'MN' },
      { label: 'USA.STATE.MISSISSIPPI', key: 'MS' },
      { label: 'USA.STATE.MISSOURI', key: 'MO' },
      { label: 'USA.STATE.MONTANA', key: 'MT' },
      { label: 'USA.STATE.NEBRASKA', key: 'NE' },
      { label: 'USA.STATE.NEVADA', key: 'NV' },
      { label: 'USA.STATE.NEW_HAMPSHIRE', key: 'NH' },
      { label: 'USA.STATE.NEW_JERSEY', key: 'NJ' },
      { label: 'USA.STATE.NEW_MEXICO', key: 'NM' },
      { label: 'USA.STATE.NEW_YORK', key: 'NY' },
      { label: 'USA.STATE.NORTH_CAROLINA', key: 'NC' },
      { label: 'USA.STATE.NORTH_DAKOTA', key: 'ND' },
      { label: 'USA.STATE.OHIO', key: 'OH' },
      { label: 'USA.STATE.OKLAHOMA', key: 'OK' },
      { label: 'USA.STATE.OREGON', key: 'OR' },
      { label: 'USA.STATE.PENNSYLVANIA', key: 'PA' },
      { label: 'USA.STATE.PUERTO_RICO', key: 'PR' },
      { label: 'USA.STATE.RHODE_ISLAND', key: 'RI' },
      { label: 'USA.STATE.SOUTH_CAROLINA', key: 'SC' },
      { label: 'USA.STATE.SOUTH_DAKOTA', key: 'SD' },
      { label: 'USA.STATE.TENNESSEE', key: 'TN' },
      { label: 'USA.STATE.TEXAS', key: 'TX' },
      { label: 'USA.STATE.UTAH', key: 'UT' },
      { label: 'USA.STATE.VERMONT', key: 'VT' },
      { label: 'USA.STATE.VIRGINIA', key: 'VA' },
      { label: 'USA.STATE.WASHINGTON', key: 'WA' },
      { label: 'USA.STATE.WEST_VIRGINIA', key: 'WV' },
      { label: 'USA.STATE.WISCONSIN', key: 'WI' },
      { label: 'USA.STATE.WYOMING', key: 'WY' },
    ];
  }

  static getProvincesForArgentina() {
    return [
      {
        label: 'ARGENTINA.STATE.CIUDAD_AUTONOMA_LA_DE_BUENOS_AIRES',
        key: 'AR-C',
      },
      { label: 'ARGENTINA.STATE.BUENOS_AIRES', key: 'AR-B' },
      { label: 'ARGENTINA.STATE.CATAMARCA', key: 'AR-K' },
      { label: 'ARGENTINA.STATE.CHACO', key: 'AR-H' },
      { label: 'ARGENTINA.STATE.CHUBUT', key: 'AR-U' },
      { label: 'ARGENTINA.STATE.CORDOBA', key: 'AR-X' },
      { label: 'ARGENTINA.STATE.CORRIENTES', key: 'AR-W' },
      { label: 'ARGENTINA.STATE.ENTRE_RIOS', key: 'AR-E' },
      { label: 'ARGENTINA.STATE.FORMOSA', key: 'AR-P' },
      { label: 'ARGENTINA.STATE.JUJUY', key: 'AR-Y' },
      { label: 'ARGENTINA.STATE.LA_PAMPA', key: 'AR-L' },
      { label: 'ARGENTINA.STATE.LA_RIOJA', key: 'AR-F' },
      { label: 'ARGENTINA.STATE.MENDOZA', key: 'AR-M' },
      { label: 'ARGENTINA.STATE.MISIONES', key: 'AR-N' },
      { label: 'ARGENTINA.STATE.NEUQUEN', key: 'AR-Q' },
      { label: 'ARGENTINA.STATE.RIO_NEGRO', key: 'AR-R' },
      { label: 'ARGENTINA.STATE.SALTA', key: 'AR-A' },
      { label: 'ARGENTINA.STATE.SAN_JUAN', key: 'AR-J' },
      { label: 'ARGENTINA.STATE.SAN_LUIS', key: 'AR-D' },
      { label: 'ARGENTINA.STATE.SANTA_CRUZ', key: 'AR-Z' },
      { label: 'ARGENTINA.STATE.SANTA_FE', key: 'AR-S' },
      { label: 'ARGENTINA.STATE.SANTIAGO_DEL_ESTERO', key: 'AR-G' },
      { label: 'ARGENTINA.STATE.TIERRA_DEL_FUEGO', key: 'AR-V' },
      { label: 'ARGENTINA.STATE.TUCUMAN', key: 'AR-T' },
    ];
  }

  static getProvincesForBrazil() {
    return [
      { label: 'BRAZIL.STATE.ACRE', key: 'AC' },
      { label: 'BRAZIL.STATE.ALAGOAS', key: 'AL' },
      { label: 'BRAZIL.STATE.AMAPA', key: 'AP' },
      { label: 'BRAZIL.STATE.AMAZONAS', key: 'AM' },
      { label: 'BRAZIL.STATE.BAHIA', key: 'BA' },
      { label: 'BRAZIL.STATE.CEARA', key: 'CE' },
      { label: 'BRAZIL.STATE.DISTRITO_FEDERAL', key: 'DF' },
      { label: 'BRAZIL.STATE.ESPIRITO_SANTO', key: 'ES' },
      { label: 'BRAZIL.STATE.GOIAS', key: 'GO' },
      { label: 'BRAZIL.STATE.MARANHAO', key: 'MA' },
      { label: 'BRAZIL.STATE.MATO_GROSSO', key: 'MT' },
      { label: 'BRAZIL.STATE.MATO_GROSSO_DO_SUL', key: 'MS' },
      { label: 'BRAZIL.STATE.MINAS_GERAIS', key: 'MG' },
      { label: 'BRAZIL.STATE.PARA', key: 'PA' },
      { label: 'BRAZIL.STATE.PARAIBA', key: 'PB' },
      { label: 'BRAZIL.STATE.PARANA', key: 'PR' },
      { label: 'BRAZIL.STATE.PERNAMBUCO', key: 'PE' },
      { label: 'BRAZIL.STATE.PIAUI', key: 'PI' },
      { label: 'BRAZIL.STATE.RIO_DE_JANEIRO', key: 'RJ' },
      { label: 'BRAZIL.STATE.RIO_GRANDE_DO_NORTE', key: 'RN' },
      { label: 'BRAZIL.STATE.RIO_GRANDE_DO_SUL', key: 'RS' },
      { label: 'BRAZIL.STATE.RONDONIA', key: 'RO' },
      { label: 'BRAZIL.STATE.RORAIMA', key: 'RR' },
      { label: 'BRAZIL.STATE.SANTA_CATARINA', key: 'SC' },
      { label: 'BRAZIL.STATE.SAO_PAULO', key: 'SP' },
      { label: 'BRAZIL.STATE.SERGIPE', key: 'SE' },
      { label: 'BRAZIL.STATE.TOCANTINS', key: 'TO' },
    ];
  }

  static getProvincesForGermany() {
    return [
      { label: 'GERMANY.STATE.BADEN_WURTTEMBERG', key: 'BW' },
      { label: 'GERMANY.STATE.BAVARIA', key: 'BY' },
      { label: 'GERMANY.STATE.BERLIN', key: 'BE' },
      { label: 'GERMANY.STATE.BRANDENBURG', key: 'BB' },
      { label: 'GERMANY.STATE.BREMEN', key: 'HB' },
      { label: 'GERMANY.STATE.HAMBURG', key: 'HH' },
      { label: 'GERMANY.STATE.HESSE', key: 'HE' },
      { label: 'GERMANY.STATE.LOWER_SAXONY', key: 'NI' },
      { label: 'GERMANY.STATE.MECKLENBURG_VORPOMMERN', key: 'MV' },
      { label: 'GERMANY.STATE.NORTH_RHINE_WESTPHALIA', key: 'NW' },
      { label: 'GERMANY.STATE.RHINELAND_PALATINE', key: 'RP' },
      { label: 'GERMANY.STATE.SAARLAND', key: 'SL' },
      { label: 'GERMANY.STATE.SAXONY', key: 'SN' },
      { label: 'GERMANY.STATE.SAXONY_ANHALT', key: 'ST' },
      { label: 'GERMANY.STATE.SCHLESWIG_HOLSTEIN', key: 'SH' },
      { label: 'GERMANY.STATE.THURINGIA', key: 'TH' },
    ];
  }

  static getProvincesForFrance() {
    return [
      { label: 'FRANCE.STATE.AUVERGNE_RHONE_ALPES', key: 'ARA' },
      { label: 'FRANCE.STATE.BOURGOGNE_FRANCHE_COMTE', key: 'BFC' },
      { label: 'FRANCE.STATE.BRETAGNE', key: 'BRE' },
      { label: 'FRANCE.STATE.CENTRE_VAL_DE_LOIRE', key: 'CVL' },
      { label: 'FRANCE.STATE.CORSE', key: 'COR' },
      { label: 'FRANCE.STATE.GRAND_EST', key: 'GES' },
      { label: 'FRANCE.STATE.GUADELOUPE', key: 'GUA' },
      { label: 'FRANCE.STATE.HAUTS_DE_FRANCE', key: 'HDF' },
      { label: 'FRANCE.STATE.ILE_DE_FRANCE', key: 'IDF' },
      { label: 'FRANCE.STATE.LA_REUNION', key: 'LRE' },
      { label: 'FRANCE.STATE.MAYOTTE', key: 'MAY' },
      { label: 'FRANCE.STATE.NORMANDIE', key: 'NOR' },
      { label: 'FRANCE.STATE.NOUVELLE_AQUITAINE', key: 'NAQ' },
      { label: 'FRANCE.STATE.OCCITANIE', key: 'OCC' },
      { label: 'FRANCE.STATE.PAYS_DE_LA_LOIRE', key: 'PDL' },
      { label: 'FRANCE.STATE.PROVENCE_ALPES_COTE_DAZUR', key: 'PAC' },
    ];
  }

  static getProvincesForAustria() {
    return [
      { label: 'AUSTRIA.STATE.BURGENLAND', key: '1' },
      { label: 'AUSTRIA.STATE.CARINTHIA', key: '2' },
      { label: 'AUSTRIA.STATE.LOWER_AUSTRIA', key: '3' },
      { label: 'AUSTRIA.STATE.UPPER_AUSTRIA', key: '4' },
      { label: 'AUSTRIA.STATE.SALZBURG', key: '5' },
      { label: 'AUSTRIA.STATE.STYRIA', key: '6' },
      { label: 'AUSTRIA.STATE.TYROL', key: '7' },
      { label: 'AUSTRIA.STATE.VORARLBERG', key: '8' },
      { label: 'AUSTRIA.STATE.VIENNA', key: '9' },
    ];
  }

  static getProvincesForNetherlands() {
    return [
      { label: 'NETHERLANDS.STATE.DRENTHE', key: 'DR' },
      { label: 'NETHERLANDS.STATE.FLEVOLAND', key: 'FL' },
      { label: 'NETHERLANDS.STATE.FRIESLAND', key: 'FR' },
      { label: 'NETHERLANDS.STATE.GELDERLAND', key: 'GE' },
      { label: 'NETHERLANDS.STATE.GRONINGEN', key: 'GR' },
      { label: 'NETHERLANDS.STATE.LIMBURG', key: 'LI' },
      { label: 'NETHERLANDS.STATE.NORTH_BRABANT', key: 'NB' },
      { label: 'NETHERLANDS.STATE.NORTH_HOLLAND', key: 'NH' },
      { label: 'NETHERLANDS.STATE.OVERIJSSEL', key: 'OV' },
      { label: 'NETHERLANDS.STATE.UTRECHT', key: 'UT' },
      { label: 'NETHERLANDS.STATE.ZEELAND', key: 'ZE' },
      { label: 'NETHERLANDS.STATE.SOUTH_HOLLAND', key: 'ZH' },
    ];
  }

  static getProvincesForPoland() {
    return [
      { label: 'POLAND.STATE.LOWER_SILESIA', key: '02' },
      { label: 'POLAND.STATE.KUYAVIA_POMERANIA', key: '04' },
      { label: 'POLAND.STATE.LUBLIN', key: '06' },
      { label: 'POLAND.STATE.LUBUSZ', key: '08' },
      { label: 'POLAND.STATE.LODZ', key: '10' },
      { label: 'POLAND.STATE.LESSER_POLAND', key: '12' },
      { label: 'POLAND.STATE.MAZOVIA', key: '14' },
      { label: 'POLAND.STATE.OPOLE', key: '16' },
      { label: 'POLAND.STATE.SUBCARPATHIA', key: '18' },
      { label: 'POLAND.STATE.PODLASKIE', key: '20' },
      { label: 'POLAND.STATE.POMERANIA', key: '22' },
      { label: 'POLAND.STATE.SILESIA', key: '24' },
      { label: 'POLAND.STATE.HOLY_CROSS', key: '26' },
      { label: 'POLAND.STATE.WARMIA_MASURIA', key: '28' },
      { label: 'POLAND.STATE.GREATER_POLAND', key: '30' },
      { label: 'POLAND.STATE.WEST_POMERANIA', key: '32' },
    ];
  }

  static getProvincesForUkraine() {
    return [
      { label: 'UKRAINE.STATE.CHERKASKA_OBLAST', key: '71' },
      { label: 'UKRAINE.STATE.CHERNIHIVSKA_OBLAST', key: '74' },
      { label: 'UKRAINE.STATE.CHERNIVETSKA_OBLAST', key: '77' },
      { label: 'UKRAINE.STATE.DNIPROPETROVSKA_OBLAST', key: '12' },
      { label: 'UKRAINE.STATE.DONETSKA_OBLAST', key: '14' },
      { label: 'UKRAINE.STATE.IVANO_FRANKIVSKA_OBLAST', key: '26' },
      { label: 'UKRAINE.STATE.KHARKIVSKA_OBLAST', key: '63' },
      { label: 'UKRAINE.STATE.KHERSONSKA_OBLAST', key: '65' },
      { label: 'UKRAINE.STATE.KHMELNYTSKA_OBLAST', key: '68' },
      { label: 'UKRAINE.STATE.KIROVOHRADSKA_OBLAST', key: '35' },
      { label: 'UKRAINE.STATE.KYIVSKA_OBLAST', key: '32' },
      { label: 'UKRAINE.STATE.LUHANSKA_OBLAST', key: '09' },
      { label: 'UKRAINE.STATE.LVIVSKA_OBLAST', key: '46' },
      { label: 'UKRAINE.STATE.MYKOLAIVSKA_OBLAST', key: '48' },
      { label: 'UKRAINE.STATE.ODESKA_OBLAST', key: '51' },
      { label: 'UKRAINE.STATE.POLTAVSKA_OBLAST', key: '53' },
      { label: 'UKRAINE.STATE.RIVNENSKA_OBLAST', key: '56' },
      { label: 'UKRAINE.STATE.SUMSKA_OBLAST', key: '59' },
      { label: 'UKRAINE.STATE.TERNOPILSKA_OBLAST', key: '61' },
      { label: 'UKRAINE.STATE.VINNYTSKA_OBLAST', key: '05' },
      { label: 'UKRAINE.STATE.VOLYNSKA_OBLAST', key: '07' },
      { label: 'UKRAINE.STATE.ZAKARPATSKA_OBLAST', key: '21' },
      { label: 'UKRAINE.STATE.ZAPORIZKA_OBLAST', key: '23' },
      { label: 'UKRAINE.STATE.ZHYTOMYRSKA_OBLAST', key: '18' },
      { label: 'UKRAINE.STATE.AVTONOMNA_RESPUBLIKA_KRYM', key: '43' },
      { label: 'UKRAINE.STATE.KYIV', key: '30' },
      { label: 'UKRAINE.STATE.SEVASTOPOL', key: '40' },
    ];
  }

  static getProvincesByCountry(country: string) {
    switch (country) {
      case CountryUtils.COUNTRY.CANADA:
        return CountryUtils.getProvincesForCanada();
      case CountryUtils.COUNTRY.USA:
        return CountryUtils.getProvincesForUsa();
      case CountryUtils.COUNTRY.ARGENTINA:
        return CountryUtils.getProvincesForArgentina();
      case CountryUtils.COUNTRY.BRAZIL:
        return CountryUtils.getProvincesForBrazil();
      case CountryUtils.COUNTRY.GERMANY:
        return CountryUtils.getProvincesForGermany();
      case CountryUtils.COUNTRY.FRANCE:
        return CountryUtils.getProvincesForFrance();
      case CountryUtils.COUNTRY.AUSTRIA:
        return CountryUtils.getProvincesForAustria();
      case CountryUtils.COUNTRY.NETHERLAND:
        return CountryUtils.getProvincesForNetherlands();
      case CountryUtils.COUNTRY.POLAND:
        return CountryUtils.getProvincesForPoland();
      case CountryUtils.COUNTRY.UKRAINE:
        return CountryUtils.getProvincesForUkraine();
      default:
        return undefined;
    }
  }

  static getSupportedCountries(): SupportedCountry[] {
    const groupedByCountryCode = groupBy(supportedCountries, 'countryCode');

    return Object.entries(groupedByCountryCode).map(([countryCode, items]) => ({
      country: countryCode,
      countryTranslationKey: items[0]?.countryTranslationKey,
      languages: items.map((item) => item.languageCode),
    }));
  }

  /**
   * Get config for specific country
   *
   * @param {string} countryCode
   * @returns {object|undefined}
   */
  static getCountryConfig(countryCode: string) {
    if (!countryCode) {
      console.warn('countryCode expected');
      return undefined;
    }

    return find(this.getSupportedCountries(), {
      country: countryCode.toLowerCase(),
    });
  }

  /**
   * Check if country code is supported
   * for example "DE" - true, "AQ" - false
   *
   * @param {string} countryCode
   * @returns {boolean}
   */
  static isSupportedCountry(countryCode: string) {
    if (!countryCode) {
      console.warn('countryCode expected');
      return false;
    }
    return Boolean(this.getCountryConfig(countryCode));
  }

  //get the country uuid based on country code
  static getRegistrationCountryUuid(country: string) {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.GERMANY:
        return '4d6e5015-80a7-8e0b-991e-440be462713e';
      case CountryUtils.COUNTRY.FRANCE:
        return '42c102ad-0cd5-3721-2119-4f13338974c7';
      case CountryUtils.COUNTRY.BRAZIL:
        return '0147379e-5769-1ad4-5af7-1e1caa55f53a';
      case CountryUtils.COUNTRY.AUSTRIA:
        return '5c856785-e523-bfed-f3c2-81e824bc1869';
      case CountryUtils.COUNTRY.NETHERLAND:
        return 'd9736851-bcf4-2b76-51a3-58e56e3e4a65';
      case CountryUtils.COUNTRY.POLAND:
        return 'b5fb92f3-b164-148a-7001-240724fe1444';
      case CountryUtils.COUNTRY.UKRAINE:
        return 'd8805a1a-4205-95d1-f1a9-b07fa49adae2';
      case CountryUtils.COUNTRY.CANADA:
        return '9bbc89c6-19f1-a6e3-907d-327066f1d864';
      case CountryUtils.COUNTRY.USA:
        return 'da57d265-58c6-beb7-6c56-bb49a009e160';
      case CountryUtils.COUNTRY.ARGENTINA:
        return '935d0ca6-5678-dbf5-fa2a-10fe928a106b';
      case CountryUtils.COUNTRY.BELGIUM:
        return '46c4f0f4-b97e-9619-3047-d1d410505bc5';
      case CountryUtils.COUNTRY.GREAT_BRITAIN:
        return '37c04c8e-8bb0-350e-681b-0d821d7af0a7';
      case CountryUtils.COUNTRY.HUNGARY:
        return '2657e0e6-d7fe-1b59-99a1-055a02acea19';
      case CountryUtils.COUNTRY.CZECH_REPUBLIC:
        return 'a8c7e5d2-59b5-53e4-e415-6a80d8d1ac1f';
      case CountryUtils.COUNTRY.JAPAN:
        return '0f59ff55-c86b-4b7b-4eaa-eb003d47dcd3';
      case CountryUtils.COUNTRY.RUSSIA:
        return '401cbf13-86bf-71ee-5c72-a0ab61d15adc';
      case CountryUtils.COUNTRY.LITHUANIA:
        return '6b98d52f-7e48-daed-7c2e-000697985794';
      case CountryUtils.COUNTRY.ITALY:
        return 'e46a8e41-35d4-795f-862d-55fc2044438a';
      case CountryUtils.COUNTRY.IRELAND:
        return '4717076c-797b-6fe9-4aba-286aab58cca9';
      case CountryUtils.COUNTRY.ROMANIA:
        return 'af590880-1ba6-f666-55f5-3f8f937e9ca5';
      case CountryUtils.COUNTRY.AUSTRALIA:
        return 'e73ea4fb-08fe-8790-81c4-86f6c18b0e9b';
      case CountryUtils.COUNTRY.TURKEY:
        return '7d90f644-9bff-fe7c-3c09-eb3163f3a8cc';
      case CountryUtils.COUNTRY.SPAIN:
        return '668129f6-791e-5dc2-751c-fcdac5b323c7';
      default:
        return '';
    }
  }

  static getAdyenClientKeyForCountry(country: string) {
    const fallbackKey = import.meta.env.VITE_SHOP_ADYEN_CLIENT_KEY;
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.CANADA:
        return import.meta.env.VITE_CA_SHOP_ADYEN_CLIENT_KEY || fallbackKey;
      case CountryUtils.COUNTRY.GERMANY:
      case CountryUtils.COUNTRY.FRANCE:
        return import.meta.env.VITE_EU_SHOP_ADYEN_CLIENT_KEY || fallbackKey;
      case CountryUtils.COUNTRY.BRAZIL:
        return import.meta.env.VITE_BR_SHOP_ADYEN_CLIENT_KEY || fallbackKey;
      case CountryUtils.COUNTRY.GREAT_BRITAIN:
        return import.meta.env.VITE_GB_SHOP_ADYEN_CLIENT_KEY || fallbackKey;
      case CountryUtils.COUNTRY.USA:
        return import.meta.env.VITE_US_SHOP_ADYEN_CLIENT_KEY || fallbackKey;
      case CountryUtils.COUNTRY.JAPAN:
        return import.meta.env.VITE_JP_SHOP_ADYEN_CLIENT_KEY;
      default:
        return fallbackKey;
    }
  }

  //get the default map provider based on country code
  static getDefaultMapProvider(country: string, isMapboxBaseMapEnabled = true) {
    const lastSelected = readLocalStorageJson<MapProvider>(
      'LAST_SELECTED_BASE_MAP_LAYER',
    );

    if (
      lastSelected &&
      (isMapboxBaseMapEnabled || lastSelected !== MapProvider.Mapbox)
    ) {
      return lastSelected;
    }
    return CountryUtils.getCountryDefaultMapProvider(country);
  }

  // Country-based default provider that ignores the persisted
  // LAST_SELECTED_BASE_MAP_LAYER value. Keeping this free of localStorage
  // prevents recursively returning the persisted "Mapbox" when the Mapbox
  // basemap is disabled.
  static getCountryDefaultMapProvider(country: string): MapProvider {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.UKRAINE:
      case CountryUtils.COUNTRY.HUNGARY:
      case CountryUtils.COUNTRY.CZECH_REPUBLIC:
      case CountryUtils.COUNTRY.JAPAN:
        return MapProvider.Google;
      default:
        return MapProvider.Bing;
    }
  }

  // get the default bbox for a country
  // return [top_right, bottom_left]
  static getBBox(country: string): LatLngBoundsLiteral {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.GERMANY:
        return [
          [55.05814, 15.04205],
          [47.27021, 5.86624],
        ];
      case CountryUtils.COUNTRY.FRANCE:
        return [
          [51.089062, 9.55932],
          [41.33374, -5.1406],
        ];
      case CountryUtils.COUNTRY.BRAZIL:
        return [
          [5.24448639569, -34.7299934555],
          [-33.7683777809, -73.9872354804],
        ];
      case CountryUtils.COUNTRY.AUSTRIA:
        return [
          [51.06901, 20.01708],
          [43.64402, 9.03076],
        ];
      case CountryUtils.COUNTRY.NETHERLAND:
        return [
          [53.78767, 7.97607],
          [50.41551, 2.48291],
        ];
      case CountryUtils.COUNTRY.POLAND:
        return [
          [55.24155, 24.5874],
          [48.47292, 13.60107],
        ];
      case CountryUtils.COUNTRY.UKRAINE:
        return [
          [55.22902, 52.88818],
          [42.40723, 10.70068],
        ];
      case CountryUtils.COUNTRY.CANADA:
        return [
          [67.28901, -64.86328],
          [46.55886, -149.23828],
        ];
      case CountryUtils.COUNTRY.USA:
        return [
          [47.295694, -108.582718],
          [32.93686, -68.131057],
        ];
      case CountryUtils.COUNTRY.ARGENTINA:
        return [
          [-20.259878, -83.964761],
          [-46.768275, -35.141518],
        ];
      case CountryUtils.COUNTRY.BELGIUM:
        return [
          [51.652961, 2.213],
          [49.483292, 6.563586],
        ];
      case CountryUtils.COUNTRY.GREAT_BRITAIN:
        return [
          [55.839083, -5.818006],
          [50.086224, 2.037218],
        ];
      case CountryUtils.COUNTRY.HUNGARY:
        return [
          [48.530812, 15.621334],
          [45.881998, 23.146969],
        ];
      case CountryUtils.COUNTRY.CZECH_REPUBLIC:
        return [
          [51.113541, 11.907955],
          [48.592615, 19.082027],
        ];
      case CountryUtils.COUNTRY.ITALY:
        return [
          [36.59347915649414, 6.536865234375],
          [47.13742446899414, 18.7591552734375],
        ];
      case CountryUtils.COUNTRY.RUSSIA:
        return [
          [54.87660665410869, 33.046875],
          [52.5897007687178, 98.96484375],
        ];
      case CountryUtils.COUNTRY.LITHUANIA:
        return [
          [53.89746856689453, 20.948762893676758],
          [56.4469108581543, 26.835590362548828],
        ];
      case CountryUtils.COUNTRY.JAPAN:
        return [
          [45.837536433053025, 150.66285543737715],
          [29.245773789607142, 126.57095930573956],
        ];
      case CountryUtils.COUNTRY.IRELAND:
        return [
          [51.348419, -10.666909],
          [54.425927, -5.398965],
        ];
      case CountryUtils.COUNTRY.AUSTRALIA:
        return [
          [-43.6345972634, 113.338953078],
          [-10.6681857235, 153.569469029],
        ];
      case CountryUtils.COUNTRY.TURKEY:
        return [
          [34.374297, 25.145167],
          [43.093958, 46.785141],
        ];
      case CountryUtils.COUNTRY.SPAIN:
        return [
          [35.219842, -10.543042],
          [44.074932, 4.269407],
        ];
      default:
        return [
          [55.05814, 15.04205],
          [47.27021, 5.86624],
        ];
    }
  }

  static getZipCodeFormat(country: string) {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.GERMANY:
        return '99999';
      case CountryUtils.COUNTRY.FRANCE:
        return '99999';
      case CountryUtils.COUNTRY.BRAZIL:
        return '99999-999';
      case CountryUtils.COUNTRY.AUSTRIA:
        return '9999';
      case CountryUtils.COUNTRY.NETHERLAND:
        return '9999 aa';
      case CountryUtils.COUNTRY.POLAND:
        return '99-999';
      case CountryUtils.COUNTRY.UKRAINE:
        return '99999';
      case CountryUtils.COUNTRY.CANADA:
        return 'a9a 9a9';
      case CountryUtils.COUNTRY.USA:
        return '99999'; // note that there might be additional -9999
      case CountryUtils.COUNTRY.ARGENTINA:
        return '9999';
      case CountryUtils.COUNTRY.BELGIUM:
        return '9999';
      case CountryUtils.COUNTRY.GREAT_BRITAIN:
        return 'a*9* 9aa';
      case CountryUtils.COUNTRY.HUNGARY:
        return '9999';
      case CountryUtils.COUNTRY.CZECH_REPUBLIC:
        return '999 99';
      case CountryUtils.COUNTRY.JAPAN:
        return '999-9999';
      case CountryUtils.COUNTRY.RUSSIA:
        return '999999';
      default:
        return '99999';
    }
  }

  static getTaxIdFormat(country: string) {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.BRAZIL:
        return '999.999.999-99';
      default:
        return '999.999.999-99';
    }
  }

  static getFieldLimitPerFarm(country: string) {
    switch (country.toLowerCase()) {
      case CountryUtils.COUNTRY.JAPAN:
        return 1000;
      default:
        return 500;
    }
  }

  // normally we should not use this (as MD would provide these data)
  // but we use it for now since MD is not yet in place
  static getCountryCurrencyCode(countryCode: string) {
    const dic = {
      [CountryUtils.COUNTRY.AUSTRALIA]: 'AUD',
      [CountryUtils.COUNTRY.CANADA]: 'CAD',
      [CountryUtils.COUNTRY.USA]: 'USD',
      [CountryUtils.COUNTRY.BRAZIL]: 'BRL',
      [CountryUtils.COUNTRY.POLAND]: 'PLN',
      [CountryUtils.COUNTRY.UKRAINE]: 'UAH',
      [CountryUtils.COUNTRY.GREAT_BRITAIN]: 'GBP',
      [CountryUtils.COUNTRY.CZECH_REPUBLIC]: 'CZK',
      [CountryUtils.COUNTRY.RUSSIA]: 'RUB',
      [CountryUtils.COUNTRY.ARGENTINA]: 'USD',
      [CountryUtils.COUNTRY.HUNGARY]: 'HUF',
      [CountryUtils.COUNTRY.JAPAN]: 'JPY',
    };

    return (dic as any)[countryCode] || 'EUR';
  }

  static getCountryPriceDecimalDigits(countryCode: string) {
    switch (countryCode) {
      case CountryUtils.COUNTRY.ARGENTINA:
        return 2;
      default:
        return 0;
    }
  }

  static getCurrencyMagentoDecimals(currencyCode: string) {
    switch (currencyCode) {
      case 'JPY':
        return 0;
      default:
        return 2;
    }
  }

  // normally we should not use this (as MD would provide these data)
  // but we use it for now since MD is not yet in place
  static getCurrencyString(currencyCode: string) {
    const dic = {
      ARS: '$',
      EUR: '€',
      AUD: 'A$',
      CAD: 'CAD $',
      PLN: 'zł',
      UAH: '₴',
      BRL: 'R$',
      GBP: '£',
      HUF: 'Ft',
      CZK: 'Kč',
      RUB: '₽',
    };

    return (dic as any)[currencyCode] || '$';
  }

  // Using browser api to format money
  //  { style: 'decimal' } as options, to omit currency symbol
  //  { minimumFractionDigits: 2, maximumFractionDigits: 2 } to force decimals
  // DE: "1.234,56 €"
  // US: "$1,234.56"
  // JP: "¥ 1,234"
  static formatMoney = (value: number, options?: Intl.NumberFormatOptions) => {
    const { country, language } = getUrlCurrentLocaleCodes();
    const currencyCode = CountryUtils.getCountryCurrencyCode(country);
    const decimalDigits = CountryUtils.getCountryPriceDecimalDigits(country);

    if (!country || !language) {
      console.warn('Cannot retrieve country or language from page url');
      return value.toString();
    }

    return new Intl.NumberFormat(`${language}-${country.toUpperCase()}`, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimalDigits,
      maximumFractionDigits: decimalDigits,
      ...options,
    }).format(value);
  };

  static planetLabCountries = [
    CountryUtils.COUNTRY.ARGENTINA,
    CountryUtils.COUNTRY.BRAZIL,
    CountryUtils.COUNTRY.JAPAN,
    CountryUtils.COUNTRY.GERMANY,
    CountryUtils.COUNTRY.FRANCE,
    CountryUtils.COUNTRY.USA,
  ];

  static onesoilCountries: string[] = [];

  static getGeoJsonUrl(countryCode: string) {
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return `${
        import.meta.env.VITE_SERVER_GIS_API_HOST
      }/field-boundary/wfs?typeName=${CountryUtils.getWMSBoundaryUrlLayerName(
        countryCode,
      )}&service=WFS&version=1.1.0&request=GetFeature&srsName=EPSG:4326&outputFormat=application/json`;
    } else if (
      !CountryUtils.onesoilCountries.length ||
      CountryUtils.onesoilCountries.includes(countryCode)
    ) {
      return `${
        import.meta.env.VITE_ONE_SOIL_FIELD_BOUNDARIES_API_HOST
      }/geoserver/basf/wfs?SERVICE=WFS&VERSION=1.0.0&REQUEST=GetFeature&TYPENAME=basf:basf_fields_2018&SRS=EPSG:4326&OUTPUTFORMAT=application/json`;
    } else return undefined;
  }

  static getCqlFilter(countryCode: string, latLng: LatLngLiteral) {
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return `&CQL_FILTER=CONTAINS(geometry,POINT(${latLng.lat} ${latLng.lng}))`;
    } else if (
      !CountryUtils.onesoilCountries.length ||
      CountryUtils.onesoilCountries.includes(countryCode)
    ) {
      return `&CQL_FILTER=CONTAINS(geom,POINT(${latLng.lng} ${latLng.lat}))`;
    } else {
      return undefined;
    }
  }

  static getWMSBoundaryUrl(countryCode: string) {
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return `${import.meta.env.VITE_SERVER_GIS_API_HOST}/field-boundary/wms`;
    } else if (
      !CountryUtils.onesoilCountries.length ||
      CountryUtils.onesoilCountries.includes(countryCode)
    ) {
      return `${
        import.meta.env.VITE_ONE_SOIL_FIELD_BOUNDARIES_API_HOST
      }/geoserver/basf/gwc/service/wms`;
    } else {
      return undefined;
    }
  }

  static getWmsWfsHeaders(countryCode: string) {
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return undefined;
    } else if (
      !CountryUtils.onesoilCountries.length ||
      CountryUtils.onesoilCountries.includes(countryCode)
    ) {
      return `Basic ${btoa(
        import.meta.env.VITE_ONE_SOIL_FIELD_BOUNDARIES_API_CREDENTIALS,
      )}`;
    } else {
      return undefined;
    }
  }

  static getWmsWfsRequestHeaders(
    countryCode: string,
    token: string,
  ): Record<string, string> {
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return {
        [import.meta.env.VITE_API_DF_TOKEN_NAME]: token,
      };
    }

    const authorizationHeader = CountryUtils.getWmsWfsHeaders(countryCode);

    return authorizationHeader ? { Authorization: authorizationHeader } : {};
  }

  static getWMSBoundaryUrlLayerName(countryCode: string) {
    const countryMap = {
      [CountryUtils.COUNTRY.ARGENTINA]: 'argentina',
      [CountryUtils.COUNTRY.BRAZIL]: 'brazil',
      [CountryUtils.COUNTRY.JAPAN]: 'japan',
      [CountryUtils.COUNTRY.GERMANY]: 'germany',
      [CountryUtils.COUNTRY.FRANCE]: 'france,belgium',
      [CountryUtils.COUNTRY.USA]: 'unitedstates',
    };
    if (CountryUtils.planetLabCountries.includes(countryCode)) {
      return (countryMap as any)[countryCode];
    } else if (
      !CountryUtils.onesoilCountries.length ||
      CountryUtils.onesoilCountries.includes(countryCode)
    ) {
      return 'basf:basf_fields_2018';
    } else {
      return undefined;
    }
  }

  static getCarbonFarmingInterventionYear(countryCode: string) {
    switch (countryCode.toLowerCase()) {
      case CountryUtils.COUNTRY.IRELAND:
        return 2023;
      case CountryUtils.COUNTRY.GREAT_BRITAIN:
        return 2024;
      default:
        return 2024;
    }
  }
}

export const getBasfConsentLabelKey = (countryCode: string) => {
  switch (countryCode.toLowerCase()) {
    default:
      return 'general.RegisterPage.basfConsentText.v3';
  }
};

export const getDefaultSoilTextureScaleCode = (countryCode: string) => {
  switch (countryCode.toLowerCase()) {
    case CountryUtils.COUNTRY.GERMANY:
      return 'SOT-DE-VDLUFA';
    case CountryUtils.COUNTRY.FRANCE:
      return 'SOT-FR-AGROEDI-SUPAGRO';
    default:
      return 'SOT';
  }
};

export const getUrlCurrentLocaleCodes = () => {
  const pathname = window.location.pathname.split('/');
  const countryRaw = pathname[1]?.length === 2 ? pathname[1] : defaultCountry;
  const localeRaw = pathname[2]?.length === 5 ? pathname[2] : defaultLocale;

  const country = countryRaw.toLowerCase();
  const locale = localeRaw.replace('-', '_').toLowerCase();
  const language = locale.split('_')[0];

  return { country, language, locale };
};

export const getCountryAlternativeFlag = (countryCode: string) => {
  switch (countryCode) {
    case CountryUtils.COUNTRY.AUSTRALIA:
      return 'au';
    default:
      return countryCode;
  }
};

const supportedCountriesRaw: SupportedCountries[] = [
  {
    countryTranslationKey: 'general.country.Argentina',
    languageTranslationKey: 'general.language.Spanish',
    countryCode: CountryUtils.COUNTRY.ARGENTINA,
    languageCode: CountryUtils.LANGUAGE.SPANISH,
    locale: `${CountryUtils.LANGUAGE.SPANISH}_${CountryUtils.COUNTRY.ARGENTINA}`,
    phoneCode: 'general.phoneCode.Argentina',
    order: 1,
  },
  {
    countryTranslationKey: 'general.country.Australia',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.AUSTRALIA,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.AUSTRALIA}`,
    phoneCode: 'general.phoneCode.Australia',
    order: 2,
  },
  {
    countryTranslationKey: 'general.country.BelgiumFrench',
    languageTranslationKey: 'general.language.French',
    countryCode: CountryUtils.COUNTRY.BELGIUM,
    languageCode: CountryUtils.LANGUAGE.FRENCH,
    locale: `${CountryUtils.LANGUAGE.FRENCH}_${CountryUtils.COUNTRY.BELGIUM}`,
    phoneCode: 'general.phoneCode.Belgium',
    order: 3,
  },
  {
    countryTranslationKey: 'general.country.BelgiumDutch',
    languageTranslationKey: 'general.language.Vlaas',
    countryCode: CountryUtils.COUNTRY.BELGIUM,
    languageCode: CountryUtils.LANGUAGE.DUTCH,
    locale: `${CountryUtils.LANGUAGE.DUTCH}_${CountryUtils.COUNTRY.BELGIUM}`,
    phoneCode: 'general.phoneCode.Belgium',
    order: 3,
  },
  {
    countryTranslationKey: 'general.country.Brazil',
    languageTranslationKey: 'general.language.Portuguese',
    countryCode: CountryUtils.COUNTRY.BRAZIL,
    languageCode: CountryUtils.LANGUAGE.PORTUGUESE,
    locale: `${CountryUtils.LANGUAGE.PORTUGUESE}_${CountryUtils.COUNTRY.BRAZIL}`,
    phoneCode: 'general.phoneCode.Brazil',
    order: 4,
  },
  {
    countryTranslationKey: 'general.country.Canada',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.CANADA,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.CANADA}`,
    phoneCode: 'general.phoneCode.Canada',
    order: 5,
  },
  {
    countryTranslationKey: 'general.country.CzechRepublic',
    languageTranslationKey: 'general.language.Czech',
    countryCode: CountryUtils.COUNTRY.CZECH_REPUBLIC,
    languageCode: CountryUtils.LANGUAGE.CZECH,
    locale: `${CountryUtils.LANGUAGE.CZECH}_${CountryUtils.COUNTRY.CZECH_REPUBLIC}`,
    phoneCode: 'general.phoneCode.CzechRepublic',
    order: 6,
  },
  {
    countryTranslationKey: 'general.country.Germany',
    languageTranslationKey: 'general.language.German',
    countryCode: CountryUtils.COUNTRY.GERMANY,
    languageCode: CountryUtils.LANGUAGE.GERMAN,
    locale: `${CountryUtils.LANGUAGE.GERMAN}_${CountryUtils.COUNTRY.GERMANY}`,
    phoneCode: 'general.phoneCode.Germany',
    order: 7,
  },
  {
    countryTranslationKey: 'general.country.France',
    languageTranslationKey: 'general.language.French',
    countryCode: CountryUtils.COUNTRY.FRANCE,
    languageCode: CountryUtils.LANGUAGE.FRENCH,
    locale: `${CountryUtils.LANGUAGE.FRENCH}_${CountryUtils.COUNTRY.FRANCE}`,
    phoneCode: 'general.phoneCode.France',
    order: 8,
  },
  {
    countryTranslationKey: 'general.country.GreatBritain',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.GREAT_BRITAIN,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.GREAT_BRITAIN}`,
    phoneCode: 'general.phoneCode.GreatBritain',
    order: 9,
  },
  {
    countryTranslationKey: 'general.country.Hungary',
    languageTranslationKey: 'general.language.Hungarian',
    countryCode: CountryUtils.COUNTRY.HUNGARY,
    languageCode: CountryUtils.LANGUAGE.HUNGARIAN,
    locale: `${CountryUtils.LANGUAGE.HUNGARIAN}_${CountryUtils.COUNTRY.HUNGARY}`,
    phoneCode: 'general.phoneCode.Hungary',
    order: 10,
  },
  {
    countryTranslationKey: 'general.country.Ireland',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.IRELAND,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.IRELAND}`,
    phoneCode: 'general.phoneCode.Ireland',
    order: 17,
  },
  {
    countryTranslationKey: 'general.country.Italy',
    languageTranslationKey: 'general.language.Italian',
    countryCode: CountryUtils.COUNTRY.ITALY,
    languageCode: CountryUtils.LANGUAGE.ITALIAN,
    locale: `${CountryUtils.LANGUAGE.ITALIAN}_${CountryUtils.COUNTRY.ITALY}`,
    phoneCode: 'general.phoneCode.Italy',
    order: 18,
  },
  {
    countryTranslationKey: 'general.country.ItalyEnglish',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.ITALY,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.ITALY}`,
    phoneCode: 'general.phoneCode.Italy',
    order: 18,
  },
  {
    countryTranslationKey: 'general.country.ItalyTurkish',
    languageTranslationKey: 'general.language.Turkish',
    countryCode: CountryUtils.COUNTRY.ITALY,
    languageCode: CountryUtils.LANGUAGE.TURKISH,
    locale: `${CountryUtils.LANGUAGE.TURKISH}_${CountryUtils.COUNTRY.ITALY}`,
    phoneCode: 'general.phoneCode.Italy',
    order: 18,
  },
  {
    countryTranslationKey: 'general.country.ItalyGreek',
    languageTranslationKey: 'general.language.Greek',
    countryCode: CountryUtils.COUNTRY.ITALY,
    languageCode: CountryUtils.LANGUAGE.GREEK,
    locale: `${CountryUtils.LANGUAGE.GREEK}_${CountryUtils.COUNTRY.ITALY}`,
    phoneCode: 'general.phoneCode.Italy',
    order: 18,
  },
  {
    countryTranslationKey: 'general.country.Japan',
    languageTranslationKey: 'general.language.Japanese',
    countryCode: CountryUtils.COUNTRY.JAPAN,
    languageCode: CountryUtils.LANGUAGE.JAPANESE,
    locale: `${CountryUtils.LANGUAGE.JAPANESE}_${CountryUtils.COUNTRY.JAPAN}`,
    phoneCode: 'general.phoneCode.Japan',
    order: 19,
  },
  {
    countryTranslationKey: 'general.country.Poland',
    languageTranslationKey: 'general.language.Polish',
    countryCode: CountryUtils.COUNTRY.POLAND,
    languageCode: CountryUtils.LANGUAGE.POLISH,
    locale: `${CountryUtils.LANGUAGE.POLISH}_${CountryUtils.COUNTRY.POLAND}`,
    phoneCode: 'general.phoneCode.Poland',
    order: 20,
  },
  {
    countryTranslationKey: 'general.country.Spain',
    languageTranslationKey: 'general.language.Spanish',
    countryCode: CountryUtils.COUNTRY.SPAIN,
    languageCode: CountryUtils.LANGUAGE.SPANISH,
    locale: `${CountryUtils.LANGUAGE.SPANISH}_${CountryUtils.COUNTRY.SPAIN}`,
    phoneCode: 'general.phoneCode.Spain',
    order: 20.1,
  },
  {
    countryTranslationKey: 'general.country.Turkey',
    languageTranslationKey: 'general.language.Turkish',
    countryCode: CountryUtils.COUNTRY.TURKEY,
    languageCode: CountryUtils.LANGUAGE.TURKISH,
    locale: `${CountryUtils.LANGUAGE.TURKISH}_${CountryUtils.COUNTRY.TURKEY}`,
    phoneCode: 'general.phoneCode.Turkish',
    order: 20.2,
  },
  {
    countryTranslationKey: 'general.country.Ukraine',
    languageTranslationKey: 'general.language.Ukrainian',
    countryCode: CountryUtils.COUNTRY.UKRAINE,
    languageCode: CountryUtils.LANGUAGE.UKRAINIAN,
    locale: `${CountryUtils.LANGUAGE.UKRAINIAN}_${CountryUtils.COUNTRY.UKRAINE}`,
    phoneCode: 'general.phoneCode.Ukraine',
    order: 22,
  },
  {
    countryTranslationKey: 'general.country.Usa',
    languageTranslationKey: 'general.language.English',
    countryCode: CountryUtils.COUNTRY.USA,
    languageCode: CountryUtils.LANGUAGE.ENGLISH,
    locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.USA}`,
    phoneCode: 'general.phoneCode.Usa',
    order: 23,
  },
  ...(import.meta.env.VITE_ENVIRONMENT !== 'PRODUCTION'
    ? [
        {
          countryTranslationKey: 'general.country.Romania',
          languageTranslationKey: 'general.language.English',
          countryCode: CountryUtils.COUNTRY.ROMANIA,
          languageCode: CountryUtils.LANGUAGE.ROMANIAN,
          locale: `${CountryUtils.LANGUAGE.ROMANIAN}_${CountryUtils.COUNTRY.ROMANIA}`,
          phoneCode: 'general.phoneCode.Romania',
          order: 21,
        },
        {
          countryTranslationKey: 'general.country.India',
          languageTranslationKey: 'general.language.English',
          countryCode: CountryUtils.COUNTRY.INDIA,
          languageCode: CountryUtils.LANGUAGE.ENGLISH,
          locale: `${CountryUtils.LANGUAGE.ENGLISH}_${CountryUtils.COUNTRY.INDIA}`,
          phoneCode: 'general.phoneCode.India',
          order: 12,
        },
        {
          countryTranslationKey: 'general.country.India',
          languageTranslationKey: 'general.language.Hindi',
          countryCode: CountryUtils.COUNTRY.INDIA,
          languageCode: CountryUtils.LANGUAGE.HINDI,
          locale: `${CountryUtils.LANGUAGE.HINDI}_${CountryUtils.COUNTRY.INDIA}`,
          phoneCode: 'general.phoneCode.India',
          order: 13,
        },
        {
          countryTranslationKey: 'general.country.India',
          languageTranslationKey: 'general.language.Punjabi',
          countryCode: CountryUtils.COUNTRY.INDIA,
          languageCode: CountryUtils.LANGUAGE.PUNJABI,
          locale: `${CountryUtils.LANGUAGE.PUNJABI}_${CountryUtils.COUNTRY.INDIA}`,
          phoneCode: 'general.phoneCode.India',
          order: 14,
        },
        {
          countryTranslationKey: 'general.country.India',
          languageTranslationKey: 'general.language.Telugu',
          countryCode: CountryUtils.COUNTRY.INDIA,
          languageCode: CountryUtils.LANGUAGE.TELUGU,
          locale: `${CountryUtils.LANGUAGE.TELUGU}_${CountryUtils.COUNTRY.INDIA}`,
          phoneCode: 'general.phoneCode.India',
          order: 15,
        },
      ]
    : []),
];

export const supportedCountries = sortBy(supportedCountriesRaw, 'order');

export const supportedPhoneCodesPerCountry = (): SupportedCountries[] => {
  return uniqBy(supportedCountries, 'countryCode');
};

export const getUserCountry = (userCountry: string) => {
  const countryCodes = CountryUtils.COUNTRY;
  return {
    userCountry,
    isGermanUser: userCountry === countryCodes.GERMANY,
    isFrenchUser: userCountry === countryCodes.FRANCE,
    isBrazilianUser: userCountry === countryCodes.BRAZIL,
    isAustrianUser: userCountry === countryCodes.AUSTRIA,
    isUkrainianUser: userCountry === countryCodes.UKRAINE,
    isCanadianUser: userCountry === countryCodes.CANADA,
    isUsaUser: userCountry === countryCodes.USA,
    isNetherlandUser: userCountry === countryCodes.NETHERLAND,
    isPolishUser: userCountry === countryCodes.POLAND,
    isArgentinianUser: userCountry === countryCodes.ARGENTINA,
    isBelgianUser: userCountry === countryCodes.BELGIUM,
    isGreatBritainUser: userCountry === countryCodes.GREAT_BRITAIN,
    isHungarianUser: userCountry === countryCodes.HUNGARY,
    isCzechRepublicUser: userCountry === countryCodes.CZECH_REPUBLIC,
    isJapanUser: userCountry === countryCodes.JAPAN,
    isRussianUser: userCountry === countryCodes.RUSSIA,
    isLithuanianUser: userCountry === countryCodes.LITHUANIA,
    isIrelandUser: userCountry === countryCodes.IRELAND,
    isRomanianUser: userCountry === countryCodes.ROMANIA,
    isIndianUser: userCountry === countryCodes.INDIA,
    isAustralianUser: userCountry === countryCodes.AUSTRALIA,
    isItalyUser: userCountry === countryCodes.ITALY,
    isSpanishUser: userCountry === countryCodes.SPAIN,
    isTurkishUser: userCountry === countryCodes.TURKEY,
    isNaUser:
      userCountry === countryCodes.USA || userCountry === countryCodes.CANADA,
  };
};
